const FORMATS = new Set(["auto", "fluid", "rectangle", "horizontal", "vertical"]);
const LAYOUTS = new Set(["in-article", "in-feed"]);
const PARSE_ALLOWED_TAGS = new Set(["ins"]);
const ALLOWED_ATTRIBUTES = new Set([
  "class",
  "style",
  "data-ad-client",
  "data-ad-slot",
  "data-ad-format",
  "data-full-width-responsive",
  "data-ad-layout",
  "data-ad-layout-key",
]);

export function parseAdSenseSlot(markup) {
  if (typeof markup !== "string" || !markup.trim() || markup.length > 4096 || typeof DOMParser === "undefined") return null;

  // Discard provider bootstrap code before parsing, then reject all non-slot tags.
  const markupWithoutScripts = markup.replace(/<script\b[^>]*>[\s\S]*?<\/script\s*>/gi, "");
  if (/<\/?script\b/i.test(markupWithoutScripts)) return null;
  for (const match of markupWithoutScripts.matchAll(/<\/?([a-z][\w:-]*)\b[^>]*>/gi)) {
    if (!PARSE_ALLOWED_TAGS.has(match[1].toLowerCase())) return null;
  }

  let document;
  try {
    document = new DOMParser().parseFromString(markupWithoutScripts, "text/html");
  } catch {
    return null;
  }

  let slot = null;
  for (const node of document.body.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const tagName = node.tagName.toLowerCase();
    if (tagName !== "ins" || slot) return null;
    slot = node;
  }

  if (!slot || slot.children.length || slot.textContent.trim()) return null;
  if ([...slot.attributes].some((attribute) => !ALLOWED_ATTRIBUTES.has(attribute.name.toLowerCase()))) return null;
  if (!(slot.getAttribute("class") || "").split(/\s+/).includes("adsbygoogle")) return null;

  const client = slot.getAttribute("data-ad-client") || "";
  const slotId = slot.getAttribute("data-ad-slot") || "";
  if (!/^ca-pub-\d{10,32}$/.test(client) || !/^\d{1,24}$/.test(slotId)) return null;

  const style = slot.getAttribute("style") || "";
  let safeStyle = { display: "block" };
  if (style && !/^\s*display\s*:\s*block\s*;?\s*$/i.test(style)) {
    // AdSense also documents fixed-size units using inline-block plus pixel dimensions.
    // Keep this grammar deliberately narrow; never pass provider CSS through to React.
    const fixedSize = style.match(/^\s*display\s*:\s*inline-block\s*;\s*width\s*:\s*(\d{1,4})px\s*;\s*height\s*:\s*(\d{1,4})px\s*;?\s*$/i);
    if (!fixedSize) return null;
    const width = Number(fixedSize[1]);
    const height = Number(fixedSize[2]);
    if (width < 1 || width > 2000 || height < 1 || height > 2000) return null;
    safeStyle = { display: "inline-block", width: `${width}px`, height: `${height}px` };
  }

  const format = slot.getAttribute("data-ad-format") || "auto";
  if (!FORMATS.has(format)) return null;

  const fullWidthResponsive = slot.getAttribute("data-full-width-responsive");
  if (fullWidthResponsive && !["true", "false"].includes(fullWidthResponsive)) return null;

  const layout = slot.getAttribute("data-ad-layout");
  if (layout && !LAYOUTS.has(layout)) return null;

  const layoutKey = slot.getAttribute("data-ad-layout-key");
  if (layoutKey && !/^[\w+/-]{1,80}$/.test(layoutKey)) return null;

  return {
    client,
    slot: slotId,
    style: safeStyle,
    format,
    fullWidthResponsive: fullWidthResponsive || undefined,
    layout: layout || undefined,
    layoutKey: layoutKey || undefined,
  };
}
